/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRemoteSessionIssuerRequest,
  GetRemoteSessionIssuerSecurity,
} from "../models/operations/getremotesessionissuer.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteSessionIssuerQuery,
  prefetchRemoteSessionIssuer,
  queryKeyRemoteSessionIssuer,
  RemoteSessionIssuerQueryData,
} from "./remoteSessionIssuer.core.js";
export {
  buildRemoteSessionIssuerQuery,
  prefetchRemoteSessionIssuer,
  queryKeyRemoteSessionIssuer,
  type RemoteSessionIssuerQueryData,
};

export type RemoteSessionIssuerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRemoteSessionIssuer remoteSessionIssuers
 *
 * @remarks
 * Get a remote_session_issuer by id, by slug, or by upstream issuer URL. Provide exactly one.
 *
 * Looking up by issuer is how an automatic setup flow decides whether an upstream authorization server already has an identity provider before creating one: a 404 means nothing describes that URL yet, so create it. Unlike id and slug, which address at most one record, several issuers may legitimately describe the same URL — a project may keep its own alongside one inherited from its organization or from the platform catalog. This returns the one this project would use, preferring project over organization over platform and, within a tier, the oldest.
 *
 * The issuer URL is canonicalized before matching: scheme and host are lowercased, the scheme's default port is dropped, and trailing slashes are stripped. http and https are deliberately NOT equated, path case is significant, and a URL carrying a query or fragment is rejected (RFC 8414 forbids both on issuer identifiers). Canonicalization applies to the supplied URL only, never to stored values, so an issuer recorded with an unusual spelling may not be found and a duplicate is created instead, which is the safe direction to fail.
 */
export function useRemoteSessionIssuer(
  request?: GetRemoteSessionIssuerRequest | undefined,
  security?: GetRemoteSessionIssuerSecurity | undefined,
  options?: QueryHookOptions<
    RemoteSessionIssuerQueryData,
    RemoteSessionIssuerQueryError
  >,
): UseQueryResult<RemoteSessionIssuerQueryData, RemoteSessionIssuerQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRemoteSessionIssuer remoteSessionIssuers
 *
 * @remarks
 * Get a remote_session_issuer by id, by slug, or by upstream issuer URL. Provide exactly one.
 *
 * Looking up by issuer is how an automatic setup flow decides whether an upstream authorization server already has an identity provider before creating one: a 404 means nothing describes that URL yet, so create it. Unlike id and slug, which address at most one record, several issuers may legitimately describe the same URL — a project may keep its own alongside one inherited from its organization or from the platform catalog. This returns the one this project would use, preferring project over organization over platform and, within a tier, the oldest.
 *
 * The issuer URL is canonicalized before matching: scheme and host are lowercased, the scheme's default port is dropped, and trailing slashes are stripped. http and https are deliberately NOT equated, path case is significant, and a URL carrying a query or fragment is rejected (RFC 8414 forbids both on issuer identifiers). Canonicalization applies to the supplied URL only, never to stored values, so an issuer recorded with an unusual spelling may not be found and a duplicate is created instead, which is the safe direction to fail.
 */
export function useRemoteSessionIssuerSuspense(
  request?: GetRemoteSessionIssuerRequest | undefined,
  security?: GetRemoteSessionIssuerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteSessionIssuerQueryData,
    RemoteSessionIssuerQueryError
  >,
): UseSuspenseQueryResult<
  RemoteSessionIssuerQueryData,
  RemoteSessionIssuerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRemoteSessionIssuerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id?: string | undefined;
      slug?: string | undefined;
      issuer?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteSessionIssuerQueryData,
): RemoteSessionIssuerQueryData | undefined {
  const key = queryKeyRemoteSessionIssuer(...queryKeyBase);

  return client.setQueryData<RemoteSessionIssuerQueryData>(key, data);
}

export function invalidateRemoteSessionIssuer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id?: string | undefined;
      slug?: string | undefined;
      issuer?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionIssuers", "get", ...queryKeyBase],
  });
}

export function invalidateAllRemoteSessionIssuer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionIssuers", "get"],
  });
}
